import React from 'react';
import { css } from '@emotion/react';
import { useLocator } from '../../contexts/franchise/locator-provider';
import { breakpoints } from '../primitives/tokens';
import SearchBar from '../primitives/search-bar';
import { GeocoderReturnable } from '../../api-utils/geocode';

export interface SearchInputAddressComponents {
    latitude: number,
    longitude: number,
    city: string,
    stateShort: string,
    state: string,
    zip: string,
    country: string
}

export interface FranchiseSearchInputProps {
    className: string
    value: string
    onChange: (addressComponents: GeocoderReturnable) => void
}

type GeocodeRawHint = {
    text: string
    magicKey?: string
    place_id?: string
} & unknown;

type GeocodeEarthHint = {
    label: string
    data: GeocodeRawHint
};

const styles = {
    container: css({
        position: 'relative',
        display: 'flex',
        [breakpoints.tb]: {
            flexDirection: 'column',
            marginBottom: '16px',
        },
    }),
};

const FranchiseSearchInput = ({
    className,
    value,
    onChange,
}: FranchiseSearchInputProps): JSX.Element => {
    const [localAddress, setLocalAddress] = React.useState(value);
    const [hints, setHints] = React.useState<GeocodeEarthHint[]>([]);
    const [loading, setLoading] = React.useState(false);

    /*
    * Get the user's geo position, to use as the 'boundary' for
    * further search results. I.e. - get results closest to the user first,
    * even if their location is not fully accurate, likely to be closer
    * to their location
    * */
    const { geo } = useLocator();

    // Update localAddress when value prop changes (e.g., when location is selected)
    React.useEffect(() => {
        setLocalAddress(value);
    }, [value]);

    // Fetch autocomplete hints
    const getHints = React.useCallback(
        (value: string) => {
            const type = value.length > 5 ? 'geocode' : '(regions)';
            // so long as the input COULD be a zip, we don't suggest addresses
            const zipRegex = /^(\d{1,5})?$/;
            const couldBeZipRegex = zipRegex.test(value);

            const url = `/api/arcgis-suggest?${new URLSearchParams({
                category: couldBeZipRegex ? 'Primary Postal,City,Subregion' : '',
                text: value,
                types: type,
                radius: '200000',
                latitude: `${geo?.latitude || ''}`,
                longitude: `${geo?.longitude || ''}`,
            }).toString()}`;

            fetch(url)
                .then(async (response) => {
                    if (!response.ok) {
                        throw new Error('Something went wrong with the call to geocode suggest');
                    }

                    const result: GeocodeRawHint[] = await response.json();

                    if (Array.isArray(result)) {
                        setHints(result.map(item => ({
                            data: item,
                            label: item.text.replace(', USA', '').replace(', CAN', ', Canada'),
                        })));
                    }
                    return null;
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setHints([]);
                });
        },
        [geo?.latitude, geo?.longitude],
    );

    const selectHint = (hint: GeocodeRawHint): void => {
        setLoading(true);

        const url = `/api/geocode?${new URLSearchParams({
            placeId: hint.place_id || '',
            magicKey: hint.magicKey || '',
            address: hint.text || '',
        }).toString()}`;

        fetch(url)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Something went wrong with the call to geocode');
                }
                const data = await response.json();

                if (data) {
                    // Use the original hint text (full selected address) but remove country suffix
                    // This preserves street addresses while removing unwanted country info
                    const cleanAddress = hint.text.replace(', USA', '').replace(', CAN', ', Canada');

                    // Create a modified data object with the clean address
                    const cleanData = {
                        ...data,
                        address: cleanAddress,
                    };

                    onChange(cleanData);
                    setHints([]);
                    setLoading(false);
                }
                return null;
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                setLoading(false);
                setHints([]);
            });
    };

    return (
        <div
            className={className}
            css={styles.container}
            onSubmit={(e) => {
                e.preventDefault();
            }}
            id="zip-search-form"
        >
            <SearchBar
                autoFocus
                placeholder="Search by Zip or City"
                bordered
                value={localAddress}
                disabled={loading}
                onChange={(address) => {
                    setLocalAddress(address);

                    if (!address) {
                        // when we clear, we'll gen an empty string, which will be falsy. We should clear the hints
                        setHints([]);
                        return;
                    }

                    const VALUE_LENGTH_TO_SHOW_HINTS = 4;

                    if (address.length < VALUE_LENGTH_TO_SHOW_HINTS) {
                        if (hints.length > 0) {
                            setHints([]);
                        }
                        return;
                    }

                    getHints(address);
                }}
                suggestions={hints}
                onSuggestionClick={(suggestion) => {
                    selectHint(suggestion as GeocodeRawHint);
                }}
            />
        </div>
    );
};

export default FranchiseSearchInput;
